import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { MainCategory, SubCategory } from '@flows.cash/types'
import { CategoryService } from '../services/categoryService'
import { useEffect } from 'react'
import { useAuthStore } from '../stores/useAuthStore'

const categoryService = CategoryService.getInstance()

export const CATEGORIES_QUERY_KEY = ['categories'] as const

export function useCategories() {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()

  // Query for fetching categories
  const {
    data: categories = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: [...CATEGORIES_QUERY_KEY, user?.id],
    queryFn: async () => {
      if (!user?.id) return []
      const { data, error } = await categoryService.fetchCategories(user.id)
      if (error) throw error
      return data || []
    },
    enabled: !!user?.id,
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  })

  // Mutation for saving all categories (use with caution)
  const { mutateAsync: saveCategories } = useMutation({
    mutationFn: async (newCategories: MainCategory[]) => {
      if (!user?.id) throw new Error('No user ID')
      const { error } = await categoryService.saveCategories(user.id, newCategories)
      if (error) throw error
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: [...CATEGORIES_QUERY_KEY, user?.id] })
    }
  })

  // Mutation for adding a single category
  const { mutateAsync: addCategory } = useMutation({
    mutationFn: async (category: MainCategory) => {
      if (!user?.id) throw new Error('No user ID')
      const { error } = await categoryService.addCategory(user.id, category)
      if (error) throw error
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: [...CATEGORIES_QUERY_KEY, user?.id] })
    }
  })

  // Mutation for adding a subcategory
  const { mutateAsync: addSubcategory } = useMutation({
    mutationFn: async ({ parentId, subcategory }: { parentId: string, subcategory: SubCategory }) => {
      if (!user?.id) throw new Error('No user ID')
      const { error } = await categoryService.addSubcategory(user.id, parentId, subcategory)
      if (error) throw error
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: [...CATEGORIES_QUERY_KEY, user?.id] })
    }
  })

  // Mutation for updating a category
  const { mutateAsync: updateCategory } = useMutation({
    mutationFn: async (category: MainCategory | SubCategory) => {
      if (!user?.id) throw new Error('No user ID')
      const { error } = await categoryService.updateCategory(user.id, category)
      if (error) throw error
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: [...CATEGORIES_QUERY_KEY, user?.id] })
    }
  })

  // Mutation for deleting a category
  const { mutateAsync: deleteCategory } = useMutation({
    mutationFn: async (categoryId: string) => {
      if (!user?.id) throw new Error('No user ID')
      const { error, message } = await categoryService.deleteCategory(user.id, categoryId)
      if (error) {
        // Include the message in the error for better user feedback
        const enhancedError = new Error(message || 'Failed to delete category')
        // @ts-ignore
        enhancedError.code = error.code
        throw enhancedError
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: [...CATEGORIES_QUERY_KEY, user?.id] })
    }
  })

  // Set up real-time subscription
  useEffect(() => {
    if (!user?.id) return

    const subscription = categoryService.subscribeToCategories(user.id, (updatedCategories) => {
      queryClient.setQueryData([...CATEGORIES_QUERY_KEY, user.id], updatedCategories)
    })

    return () => {
      subscription.then(sub => sub.unsubscribe())
    }
  }, [user?.id, queryClient])

  return {
    categories,
    isLoading,
    error,
    saveCategories,
    addCategory,
    addSubcategory,
    updateCategory,
    deleteCategory,
    refetch
  }
} 